export const formatDate = (date: Date | string, timezone?: string): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    // Add timezone if provided
    if (timezone) {
      options.timeZone = timezone;
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  } catch (error) {
    return 'Invalid date';
  }
};

export const formatDateOnly = (dateStr: string | Date, timezone?: string): string => {
  try {
    // If it's a date string (YYYY-MM-DD), parse it directly
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      // Create date using local timezone (month is 0-based in JS Date)
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };
      
      if (timezone) {
        options.timeZone = timezone;
      }
      
      return new Intl.DateTimeFormat('en-US', options).format(new Date(year, month - 1, day));
    }
    
    // Otherwise, use the standard date formatting
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (timezone) {
      options.timeZone = timezone;
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    return 'Invalid date';
  }
};

// Convert a UTC date string to a specified timezone
export const convertToTimezone = (date: string | Date, timezone = 'UTC'): Date => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date provided');
  }
  
  // If no specific timezone, just return the original date
  if (!timezone || timezone === 'UTC') {
    return dateObj;
  }
  
  // Use the Intl.DateTimeFormat to apply the timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });
  
  // Get the parts
  const parts = formatter.formatToParts(dateObj);
  
  // Extract values
  const year = Number(parts.find(part => part.type === 'year')?.value || 0);
  const month = Number(parts.find(part => part.type === 'month')?.value || 0) - 1; // JS months are 0-indexed
  const day = Number(parts.find(part => part.type === 'day')?.value || 0);
  const hour = Number(parts.find(part => part.type === 'hour')?.value || 0);
  const minute = Number(parts.find(part => part.type === 'minute')?.value || 0);
  const second = Number(parts.find(part => part.type === 'second')?.value || 0);

  // Create a new date object with the timezone-adjusted values
  return new Date(year, month, day, hour, minute, second);
};

// Format a date for database storage (UTC ISO format)
export const formatForStorage = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date for storage');
  }
  return dateObj.toISOString();
};

// Get the current user's timezone setting from the app settings
export const getUserTimezone = (): string => {
  try {
    // Try to get from local storage if available
    const settingsString = localStorage.getItem('user_settings');
    if (settingsString) {
      const settings = JSON.parse(settingsString);
      if (settings.timezone) {
        return settings.timezone;
      }
    }
    
    // Fallback to browser's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    // If any error occurs, fallback to UTC
    return 'UTC';
  }
};

// Format date with the user's timezone
export const formatWithUserTimezone = (date: Date | string): string => {
  return formatDate(date, getUserTimezone());
};

// Format date-only with the user's timezone
export const formatDateOnlyWithUserTimezone = (date: Date | string): string => {
  return formatDateOnly(date, getUserTimezone());
}; 